// ============================================================================
// KASSA HOLATI: qurilma, katalog, PIN bilan kirgan xodim, smena, sinxronlash.
// ============================================================================

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { ApiError } from '@onepos/client';
import { deviceApi, loginDevice, withTimeout } from './device';
import { setStaffSession } from '../lib/api';
import { meta } from './idb';
import { type PollPlan, usePolling } from './poll';
import { checkPasswordOffline, checkPinOffline, hasPasswordOffline, prunePins, rememberPassword, rememberPin } from './pin';
import { engine, isDeviceLost, isNetwork, outboxItems, perform, pruneOrders } from './sync';
import type { OutboxKind, PerformResult, SyncStatus } from './sync';
import { permSet, readSettings } from './logic';
import type { Boot, BootStaff, KassaSettings, PermSet, ShiftInfo } from './logic';

const BOOT_POLL: PollPlan = { intervalMs: 5 * 60_000, hiddenMs: null, delayFirst: true };

export interface StaffCtx {
  id: string;
  full_name: string;
  role_name: string;
  session: string | null;
  perms: { key: string; limit: number | null }[];
  /** Internet yo'qligida kirgan (amallar navbat orqali ketadi) */
  offline: boolean;
  /** Boshqaruv paneliga kira oladi */
  panel: boolean;
}

/** unpaired — restoran logini kerak; pending — qurilma tasdiqlanmagan */
export type Phase = 'loading' | 'unpaired' | 'need_online' | 'pending' | 'ready';

export interface StepStaff {
  id: string;
  full_name: string;
}

/** PIN (yoki keyingi qadam) natijasi */
export type LoginResult =
  | { kind: 'ok' }
  | { kind: 'wrong' }
  | { kind: 'more' }
  | { kind: 'password'; ticket: string | null; staff: StepStaff; left?: number; offline?: boolean }
  | { kind: 'setup'; ticket: string; staff: StepStaff }
  | { kind: 'approve'; ticket: string; staff: StepStaff }
  | { kind: 'approved' }
  | { kind: 'locked'; until: string | null; staff?: StepStaff }
  | { kind: 'error'; code: string };

interface ServerLogin {
  ok: boolean;
  error?: string;
  step?: 'done' | 'password' | 'setup' | 'approve' | 'approved';
  session?: string;
  ticket?: string;
  locked_until?: string;
  attempts_left?: number;
  panel?: boolean;
  staff?: { id: string; full_name: string; role_name: string };
  permissions?: { key: string; limit: number | null }[];
}

interface KassaValue {
  phase: Phase;
  message: string | undefined;
  boot: Boot | null;
  settings: KassaSettings;
  staff: StaffCtx | null;
  perms: PermSet;
  shift: ShiftInfo | null;
  sync: SyncStatus;
  ordersVersion: number;
  loginRestaurant: (login: string, password: string) => Promise<void>;
  reloadBoot: () => Promise<Boot | null>;
  /** PIN oynasi ochilganda: kira oladigan xodimlar ro'yxati (yangi qo'shilgan xodim darhol kiradi) */
  refreshStaff: () => Promise<void>;
  login: (pin: string, final: boolean) => Promise<LoginResult>;
  loginPassword: (step: { ticket: string | null; staff: StepStaff }, password: string) => Promise<LoginResult>;
  setupPassword: (ticket: string, password: string) => Promise<LoginResult>;
  approveDevice: (ticket: string, kind: string, name: string, branch: string | null, replace?: string | null) => Promise<LoginResult>;
  forgetDevice: () => Promise<void>;
  lock: (message?: string) => void;
  act: <T>(kind: OutboxKind, payload: Record<string, unknown>, label: string) => Promise<PerformResult<T>>;
  setShift: (s: ShiftInfo | null) => Promise<void>;
  bumpOrders: () => void;
}

const Ctx = createContext<KassaValue | null>(null);
const STAFF_KEY = 'onepos.kassa.staff';

// Kirgan xodim: kassa/oshxonada — shu oyna yopilguncha (sessionStorage);
// boshqaruv qurilmasida (ega/menejer telefoni) — "Chiqish" bosilguncha (localStorage).
function loadStaff(): StaffCtx | null {
  for (const store of [() => sessionStorage, () => localStorage]) {
    try {
      const raw = store().getItem(STAFF_KEY);
      if (raw) return JSON.parse(raw) as StaffCtx;
    } catch {
      /* saqlash yo'q */
    }
  }
  return null;
}

function saveStaff(s: StaffCtx | null, persistent: boolean) {
  for (const [store, use] of [
    [() => sessionStorage, !persistent],
    [() => localStorage, persistent],
  ] as const) {
    try {
      if (s && use) store().setItem(STAFF_KEY, JSON.stringify(s));
      else store().removeItem(STAFF_KEY);
    } catch {
      /* saqlash yo'q — sahifa yangilansa qayta PIN */
    }
  }
}

function initialStaff(): StaffCtx | null {
  const s = loadStaff();
  setStaffSession(s && !s.offline ? s.session : null);
  return s;
}

const EMPTY_SETTINGS = readSettings({});

export function KassaProvider({ children }: { children: ReactNode }) {
  const [phase, setPhase] = useState<Phase>('loading');
  const [message, setMessage] = useState<string>();
  const [boot, setBoot] = useState<Boot | null>(null);
  const [staff, setStaffState] = useState<StaffCtx | null>(initialStaff);
  const [shift, setShiftState] = useState<ShiftInfo | null>(null);
  const [sync, setSync] = useState<SyncStatus>(engine.current);
  const [ordersVersion, setOrdersVersion] = useState(0);
  const bootRef = useRef<Boot | null>(null);

  const setStaff = useCallback((s: StaffCtx | null) => {
    saveStaff(s, bootRef.current?.terminal.kind === 'manager');
    setStaffSession(s && !s.offline ? s.session : null);
    setStaffState(s);
  }, []);

  const lock = useCallback(
    (msg?: string) => {
      const s = loadStaff();
      if (s?.session) void deviceApi.rpc('pos_logout', { p_session: s.session }).catch(() => undefined);
      setStaff(null);
      setMessage(msg);
    },
    [setStaff],
  );

  const setShift = useCallback(async (s: ShiftInfo | null) => {
    await meta.set('shift', s);
    setShiftState(s);
  }, []);

  const deviceLost = useCallback(
    (msg = 'Bu qurilma restorandan uzilgan. Restoran logini bilan qayta kiring.') => {
      engine.setDeviceLost(true);
      setStaff(null);
      setMessage(msg);
      setPhase('unpaired');
    },
    [setStaff],
  );

  /** Serverdan katalog; internet bo'lmasa — keshdagisi */
  const reloadBoot = useCallback(async (): Promise<Boot | null> => {
    try {
      const b = await withTimeout(deviceApi.rpc<Boot>('pos_bootstrap'), 15_000);
      if (b.approved === false) {
        // Tasdiqlanmagan: katalog yo'q, keshga yozmaymiz
        engine.markOnline(true);
        engine.setDeviceLost(false);
        bootRef.current = b;
        setBoot(b);
        return b;
      }
      await meta.set('boot', b);
      await prunePins(b.staff);
      void pruneOrders(b.business_date);
      // Navbatda smena amali bo'lmasa — server holati to'g'ri
      const busy = (await outboxItems()).some((i) => i.state === 'pending' && (i.kind === 'shift_open' || i.kind === 'shift_close'));
      if (!busy) {
        await meta.set('shift', b.shift);
        setShiftState(b.shift);
      }
      // Kirgan xodim o'chirilgan yoki bu qurilmadan chiqarilgan bo'lsa — qulflash
      const cur = loadStaff();
      if (cur && !b.staff.some((s) => s.id === cur.id)) lock('Xodim bu qurilmada ishlay olmaydi.');
      engine.markOnline(true);
      engine.setDeviceLost(false);
      bootRef.current = b;
      setBoot(b);
      return b;
    } catch (e) {
      if (isDeviceLost(e)) {
        deviceLost();
        return null;
      }
      if (!isNetwork(e)) throw e;
      engine.markOnline(false);
      return bootRef.current;
    }
  }, [deviceLost, lock]);

  // Ishga tushish
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!deviceApi.session) {
        setPhase('unpaired');
        return;
      }
      const cached = (await meta.get<Boot>('boot').catch(() => undefined)) ?? null;
      const cachedShift = (await meta.get<ShiftInfo | null>('shift').catch(() => undefined)) ?? null;
      if (cached && alive) {
        bootRef.current = cached;
        setBoot(cached);
        setShiftState(cachedShift);
      }
      let fresh: Boot | null = null;
      try {
        fresh = await reloadBoot();
      } catch (e) {
        if (alive) setMessage(e instanceof Error ? e.message : String(e));
      }
      if (!alive || engine.current.deviceLost) return;
      if (!fresh && !cached) {
        setPhase('need_online');
        return;
      }
      if (fresh?.approved === false) {
        setPhase('pending');
        return;
      }
      setPhase('ready');
      engine.start();
    })();
    return () => {
      alive = false;
    };
  }, [reloadBoot]);

  useEffect(() => engine.subscribe(setSync), []);

  useEffect(
    () =>
      engine.onApplied((kind) => {
        if (kind === 'shift_open' || kind === 'shift_close') {
          void meta.get<ShiftInfo | null>('shift').then((s) => setShiftState(s ?? null));
        } else {
          setOrdersVersion((v) => v + 1);
        }
      }),
    [],
  );

  useEffect(() => {
    if (sync.deviceLost && phase === 'ready') deviceLost();
  }, [sync.deviceLost, phase, deviceLost]);

  // Katalog har 5 daqiqada yangilanadi — faqat ekran ko'rinib turganda; ko'ringanda eskirgan bo'lsa darhol (Q-052)
  const refreshBoot = useCallback(async () => {
    await reloadBoot().catch(() => undefined);
    return true;
  }, [reloadBoot]);
  usePolling(refreshBoot, phase === 'ready', BOOT_POLL);

  const startWith = useCallback(async () => {
    engine.setDeviceLost(false);
    setMessage(undefined);
    setPhase('loading');
    const b = await reloadBoot();
    if (!b) {
      setPhase('need_online');
      return;
    }
    if (b.approved === false) {
      setPhase('pending');
      return;
    }
    setPhase('ready');
    engine.start();
  }, [reloadBoot]);

  const loginRestaurant = useCallback(
    async (login: string, password: string) => {
      await loginDevice(login, password);
      await startWith();
    },
    [startWith],
  );

  /** Qurilmani unutish (restoran logini oynasiga qaytish) — faqat tasdiqlanmagan qurilmada */
  const forgetDevice = useCallback(async () => {
    await deviceApi.signOut().catch(() => undefined);
    setStaff(null);
    setBoot(null);
    bootRef.current = null;
    setPhase('unpaired');
  }, [setStaff]);

  /** Serverning kirish javobini qabul qilish */
  const accept = useCallback(
    async (res: ServerLogin, pin: string | null, password: string | null): Promise<LoginResult> => {
      if (!res.ok) {
        if (res.error === 'PASSWORD_LOCKED') return { kind: 'locked', until: res.locked_until ?? null, staff: res.staff };
        if (res.error === 'PASSWORD_WRONG') return { kind: 'error', code: `PASSWORD_WRONG:${res.attempts_left ?? 0}` };
        return { kind: 'error', code: res.error ?? 'SERVER_ERROR' };
      }
      const st = res.staff ? { id: res.staff.id, full_name: res.staff.full_name } : { id: '', full_name: '' };
      if (res.step === 'password' && res.ticket) return { kind: 'password', ticket: res.ticket, staff: st };
      if (res.step === 'setup' && res.ticket) return { kind: 'setup', ticket: res.ticket, staff: st };
      if (res.step === 'approve' && res.ticket) return { kind: 'approve', ticket: res.ticket, staff: st };
      if (res.step === 'approved') {
        // Tasdiqlandi, lekin tasdiqlagan xodim bu turdagi qurilmada ishlamaydi: PIN oynasida qolamiz
        await deviceApi.refresh().catch(() => null);
        const nb = await reloadBoot();
        if (nb && nb.approved !== false) {
          setPhase('ready');
          engine.start();
        }
        return { kind: 'approved' };
      }
      if (res.session && res.staff) {
        let b = bootRef.current;
        if (b?.approved === false) {
          // Shu yerda tasdiqlandi: token va katalog yangilanadi
          await deviceApi.refresh().catch(() => null);
          b = await reloadBoot();
          if (b && b.approved !== false) {
            setPhase('ready');
            engine.start();
          }
        }
        const cached = b?.staff.find((x) => x.id === res.staff!.id);
        if (b && cached && pin) void rememberPin(b.terminal.id, cached, pin);
        if (b && cached && password) void rememberPassword(b.terminal.id, cached.id, cached.password_set_at, password);
        setStaff({
          id: res.staff.id,
          full_name: res.staff.full_name,
          role_name: res.staff.role_name,
          session: res.session,
          perms: res.permissions ?? [],
          offline: false,
          panel: Boolean(res.panel),
        });
        setMessage(undefined);
        engine.markOnline(true);
        return { kind: 'ok' };
      }
      return { kind: 'error', code: 'SERVER_ERROR' };
    },
    [reloadBoot, setStaff],
  );

  const refreshStaff = useCallback(async (): Promise<void> => {
    const b = bootRef.current;
    if (!b || b.approved === false || !navigator.onLine) return;
    try {
      const list = await withTimeout(deviceApi.rpc<BootStaff[]>('pos_staff_cache'), 8_000);
      // Shu orada katalog yangilangan bo'lsa — o'sha yangiroq
      if (!Array.isArray(list) || bootRef.current !== b) return;
      const nb: Boot = { ...b, staff: list };
      bootRef.current = nb;
      setBoot(nb);
      await meta.set('boot', nb);
      await prunePins(list);
    } catch {
      /* internet yoki server — keshdagi ro'yxat qoladi */
    }
  }, []);

  // PIN bosqichidan keyingi qadamlar uchun: PIN offline tekshiruvda kerak bo'ladi
  const pinRef = useRef<string | null>(null);

  const login = useCallback(
    async (pin: string, final: boolean): Promise<LoginResult> => {
      const b = bootRef.current;
      if (!b) return { kind: 'wrong' };
      const pending = b.approved === false;
      const maxLen = pending ? 6 : Math.max(4, ...b.staff.map((s) => s.pin_length));
      const longerPossible = !final && pin.length < maxLen;
      const localId = pending ? null : await checkPinOffline(b.terminal.id, pin).catch(() => null);
      const fromCache = (id: string): BootStaff | undefined => b.staff.find((s) => s.id === id);
      pinRef.current = pin;

      if (navigator.onLine) {
        // Avval shu kassada kirmagan va PIN uzunroq bo'lishi mumkin — serverga urinish sarflamaymiz
        if (!localId && longerPossible) return { kind: 'more' };
        try {
          const res = await withTimeout(deviceApi.rpc<ServerLogin>('pos_login', { p_pin: pin }), 12_000);
          if (!res.ok && res.error === 'INVALID_PIN') return longerPossible ? { kind: 'more' } : { kind: 'wrong' };
          return await accept(res, pin, null);
        } catch (e) {
          if (isDeviceLost(e)) {
            deviceLost();
            return { kind: 'wrong' };
          }
          if (!isNetwork(e)) throw e;
          engine.markOnline(false);
        }
      }

      // Offline: faqat shu kassada avval online kirgan xodimlar
      if (pending) return { kind: 'error', code: 'NETWORK' };
      const s = localId ? fromCache(localId) : undefined;
      if (s) {
        if (s.password === 'setup') return { kind: 'error', code: 'OFFLINE_PASSWORD_SETUP' };
        if (s.password === 'password') {
          if (!(await hasPasswordOffline(s.id))) return { kind: 'error', code: 'OFFLINE_PASSWORD_UNKNOWN' };
          return { kind: 'password', ticket: null, staff: { id: s.id, full_name: s.full_name }, offline: true };
        }
        setStaff({ id: s.id, full_name: s.full_name, role_name: s.role_name, session: null, perms: s.permissions, offline: true, panel: Boolean(s.panel) });
        setMessage(undefined);
        return { kind: 'ok' };
      }
      return longerPossible ? { kind: 'more' } : { kind: 'wrong' };
    },
    [accept, deviceLost, setStaff],
  );

  const loginPassword = useCallback(
    async (step: { ticket: string | null; staff: StepStaff }, password: string): Promise<LoginResult> => {
      const b = bootRef.current;
      if (!b) return { kind: 'wrong' };
      if (step.ticket) {
        try {
          const res = await withTimeout(
            deviceApi.rpc<ServerLogin>('pos_login_password', { p_ticket: step.ticket, p_password: password }),
            12_000,
          );
          if (!res.ok && res.error === 'PASSWORD_WRONG') return { kind: 'password', ticket: step.ticket, staff: step.staff, left: res.attempts_left };
          return await accept(res, pinRef.current, password);
        } catch (e) {
          if (!isNetwork(e)) throw e;
          engine.markOnline(false);
        }
      }
      // Offline parol
      const s = b.staff.find((x) => x.id === step.staff.id);
      if (!s) return { kind: 'error', code: 'NETWORK' };
      const r = await checkPasswordOffline(b.terminal.id, s.id, password);
      if (r.ok) {
        setStaff({ id: s.id, full_name: s.full_name, role_name: s.role_name, session: null, perms: s.permissions, offline: true, panel: Boolean(s.panel) });
        setMessage(undefined);
        return { kind: 'ok' };
      }
      if (r.locked) return { kind: 'locked', until: r.until ? new Date(r.until).toISOString() : null, staff: step.staff };
      return { kind: 'password', ticket: null, staff: step.staff, left: r.left, offline: true };
    },
    [accept, setStaff],
  );

  const setupPassword = useCallback(
    async (ticket: string, password: string): Promise<LoginResult> => {
      const res = await withTimeout(deviceApi.rpc<ServerLogin>('pos_password_setup', { p_ticket: ticket, p_new: password }), 12_000);
      return accept(res, pinRef.current, password);
    },
    [accept],
  );

  const approveDevice = useCallback(
    async (ticket: string, kind: string, name: string, branch: string | null, replace: string | null = null): Promise<LoginResult> => {
      const res = await withTimeout(
        deviceApi.rpc<ServerLogin>('pos_device_approve', { p_ticket: ticket, p_kind: kind, p_name: name, p_branch: branch, p_replace: replace }),
        15_000,
      );
      return accept(res, pinRef.current, null);
    },
    [accept],
  );

  const act = useCallback(
    async <T,>(kind: OutboxKind, payload: Record<string, unknown>, label: string): Promise<PerformResult<T>> => {
      const s = loadStaff();
      if (!s) throw new ApiError('SESSION_REQUIRED', 401);
      try {
        return await perform<T>(kind, payload, { staffId: s.id, session: s.session }, label);
      } catch (e) {
        if (e instanceof ApiError && e.code === 'SESSION_INVALID') lock('Kirish muddati tugadi — PIN’ni qayta kiriting.');
        if (isDeviceLost(e)) deviceLost();
        throw e;
      }
    },
    [deviceLost, lock],
  );

  const value = useMemo<KassaValue>(
    () => ({
      phase,
      message,
      boot,
      settings: boot ? readSettings(boot.settings) : EMPTY_SETTINGS,
      staff,
      perms: permSet(staff?.perms ?? []),
      shift,
      sync,
      ordersVersion,
      loginRestaurant,
      reloadBoot,
      refreshStaff,
      login,
      loginPassword,
      setupPassword,
      approveDevice,
      forgetDevice,
      lock,
      act,
      setShift,
      bumpOrders: () => setOrdersVersion((v) => v + 1),
    }),
    [phase, message, boot, staff, shift, sync, ordersVersion, loginRestaurant, reloadBoot, refreshStaff, login, loginPassword, setupPassword, approveDevice, forgetDevice, lock, act, setShift],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useKassa(): KassaValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useKassa KassaProvider ichida ishlatiladi');
  return v;
}
